import { useQuery } from "@tanstack/react-query";
import { getActiveRegions } from "../services/get-active-regions";
import { Region } from "../types/region";

export type CurrencyOption = { label: string; value: string };

// Human-readable names from the browser ("INR" -> "Indian Rupee") so we don't
// maintain a hand-written code->label map.
const currencyNames = new Intl.DisplayNames(["en"], { type: "currency" });

/**
 * Currencies the system supports = distinct currencies of ACTIVE regions,
 * straight from GET /region/active. The server validates coupon currencies
 * against the same source (RegionService.getSupportedCurrencies), so this
 * dropdown can never offer a value the API would reject — one source of
 * truth, no generated constant files.
 */
export default function useSupportedCurrencies(): {
  currencies: CurrencyOption[];
  isLoading: boolean;
} {
  // Same query key/cache as useActiveRegions, but THIS observer treats the
  // data as immediately stale: the dropdown renders the cached list with no
  // flicker, while every mount (each drawer open) revalidates in the
  // background — a newly activated region's currency appears within moments
  // instead of waiting out the 30-minute staleTime other consumers use.
  const { data: regions, isLoading } = useQuery<Region[]>({
    queryKey: ["regions", "active"],
    queryFn: () => getActiveRegions(),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const codes = [
    ...new Set((regions || []).map((region) => region.currency.toUpperCase())),
  ];

  const currencies = codes.map((code) => {
    let label = code;
    try {
      label = currencyNames.of(code) ?? code;
    } catch {
      // Non-ISO code in the Region table — show it as-is rather than crash.
    }
    return { label, value: code };
  });

  return { currencies, isLoading };
}
